import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/auth/LoginPage";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import UnderwriterDashboard from "@/pages/underwriter/UnderwriterDashboard";
import OrderInspection from "@/pages/underwriter/OrderInspection";
import ReviewInspection from "@/pages/underwriter/ReviewInspection";
import ReportPreview from "@/pages/underwriter/ReportPreview";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AnalyticsDashboard from "@/pages/analytics/AnalyticsDashboard";
import InspectionPortal from "@/pages/insured/InspectionPortal";
import SurveyFormPortal from "@/pages/insured/SurveyFormPortal";
import PlatformAdmin from "@/pages/platform/PlatformAdmin";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import InsuredHub from "@/pages/insured/InsuredHub";
import RecPortal from "@/pages/insured/RecPortal";
import RecommendationsDashboard from "@/pages/underwriter/RecommendationsDashboard";
import BrokerPortal from "@/pages/broker/BrokerPortal";
import TrustPage from "@/pages/TrustPage";
import LCDashboard from "@/pages/lc/LCDashboard";
import PreBriefingView from "@/pages/lc/PreBriefingView";
import FieldVisitView from "@/pages/lc/FieldVisitView";
import "@/App.css";
import { Toaster } from "sonner";

const LC_ROLES = ["lc_manager", "lc_staff"];
const STAFF_ROLES = ["admin", "underwriter", "loss_control", "lc_manager", "lc_staff"];

function ProtectedRoute({ children, requiredRole }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin h-8 w-8 border-4 border-navy border-t-transparent rounded-full"></div></div>;
  if (!user) return <Navigate to="/login" replace />;

  // LC roles get their own dedicated dashboard, not the underwriter one
  // (only redirect from the dashboard index — review/report pages stay accessible)
  if (requiredRole === "staff" && LC_ROLES.includes(user.role) && location.pathname === "/dashboard") {
    return <Navigate to="/lc-dashboard" replace />;
  }

  // Brokers only get the broker portal
  if (requiredRole === "staff" && user.role === "broker") {
    return <Navigate to="/broker" replace />;
  }

  if (requiredRole === "lc" && !LC_ROLES.includes(user.role) && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredRole && requiredRole !== "staff" && requiredRole !== "lc" &&
      user.role !== requiredRole && !(requiredRole === "staff" && STAFF_ROLES.includes(user.role))) {
    return <Navigate to={user.role === "platform_admin" ? "/platform" : "/dashboard"} replace />;
  }

  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={user ? <Navigate to={user.role === "platform_admin" ? "/platform" : user.role === "broker" ? "/broker" : LC_ROLES.includes(user.role) ? "/lc-dashboard" : "/dashboard"} replace /> : <LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/platform" element={<PlatformAdmin />} />
      <Route path="/dashboard" element={<ProtectedRoute requiredRole="staff"><UnderwriterDashboard /></ProtectedRoute>} />
      <Route path="/dashboard/order" element={<ProtectedRoute requiredRole="staff"><OrderInspection /></ProtectedRoute>} />
      <Route path="/dashboard/review/:inspectionId" element={<ProtectedRoute requiredRole="staff"><ReviewInspection /></ProtectedRoute>} />
      <Route path="/dashboard/report/:inspectionId" element={<ProtectedRoute requiredRole="staff"><ReportPreview /></ProtectedRoute>} />
      <Route path="/lc-dashboard" element={<ProtectedRoute requiredRole="lc"><LCDashboard /></ProtectedRoute>} />
      <Route path="/lc/briefing/:inspectionId" element={<ProtectedRoute requiredRole="lc"><PreBriefingView /></ProtectedRoute>} />
      <Route path="/lc/visit/:inspectionId" element={<ProtectedRoute requiredRole="lc"><FieldVisitView /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute requiredRole="staff"><AnalyticsDashboard /></ProtectedRoute>} />
      <Route path="/inspection/:token" element={<InspectionPortal />} />
      <Route path="/survey/:token" element={<SurveyFormPortal />} />
      <Route path="/hub/:hubToken" element={<InsuredHub />} />
      <Route path="/recs/:recToken" element={<RecPortal />} />
      <Route path="/dashboard/recommendations" element={<ProtectedRoute requiredRole="staff"><RecommendationsDashboard /></ProtectedRoute>} />
      <Route path="/broker" element={<ProtectedRoute requiredRole="broker"><BrokerPortal /></ProtectedRoute>} />
      <Route path="/trust" element={<TrustPage />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
